import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import { useQueryClient } from '@tanstack/react-query';

import { useStudio } from '../../queries';
import { queryKeys } from '../../queries/keys';
import { ApiResponseError } from '../../api/envelope';
import type { StoryOutlineSection, TextOperationResult, TextOperationType } from '../../domain';
import { useCaptionHandoffStore } from '../../store/captionHandoffStore';

import { TopBar } from '../shared/topbar/TopBar';
import { SwatchPanel } from '../shared/panels/SwatchPanel';
import { SelectionChips } from '../shared/panels/SelectionChips';
import { LabLoadingSkeleton } from '../shared/layout/LabLoadingSkeleton';
import { ActionPanel } from '../shared/panels/ActionPanel';
import { AgentBar } from '../shared/agent-bar/AgentBar';
import { RailSplitter } from '../shared/layout/RailSplitter';
import { loadRailWidth, saveRailWidth } from '../shared/layout/railWidths';
import {
  buildStudioMetadata,
  describeSwatches,
  studioParamsFromSelections,
  type SwatchSelections,
} from '../shared/panels/swatchSelection';

import { CanvasToolbar } from './canvas/CanvasToolbar';
import { LexicalDocCanvas } from './canvas/LexicalDocCanvas';
import { buildInitialConfig } from './canvas/lexicalConfig';
import { useMobileShell } from '../../shell/useMobileShell';
import { MobileShell } from '../../shell/MobileShell';
import { TextOperationPanel, StudioRequestContext } from './operations';
import { TextOperationDock, pickDefaultTextOperation } from './TextOperationDock';
import { StorytellerRail } from './storyteller/StorytellerRail';
import { writerExamplePromptsForStudio } from './examplePrompts';
import { useWriterLabSession } from './hooks';

interface Props {
  studioId?: string;
}

/**
 * Writer's Lab shell — parity with Image Lab: TopBar · Swatch rail · Lexical
 * document canvas · Operations/Agents rail · Agent bar. The <LexicalComposer>
 * is hoisted here (not the canvas) so the rail's operation panels share the
 * editor and can dispatch AI_INSERT. Keyed by studioId so switching studios
 * yields a fresh editor + session.
 */
export const WriterLab: React.FC<Props> = ({ studioId = 'storyteller' }) => {
  const { data: studio, isLoading, error } = useStudio(studioId);
  const { isMobileShell } = useMobileShell();

  const { sessionId, documentId, captureDocument, ensureSession, isCreatingSession, sessionError } =
    useWriterLabSession(studioId);
  const qc = useQueryClient();
  const isStoryteller = studioId === 'storyteller';

  const [activeKind, setActiveKind] = useState<TextOperationType | null>(null);
  const [swatchSelections, setSwatchSelections] = useState<SwatchSelections>({});
  const [referenceImageId, setReferenceImageId] = useState<string | undefined>(undefined);

  // Storyteller sub-surface state.
  const [storytellerConceptId, setStorytellerConceptId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<StoryOutlineSection | null>(null);
  const [promptSeedKey, setPromptSeedKey] = useState(0);
  // Example-prompt seed for the blank-doc chips (non-Storyteller studios).
  const [exampleSeed, setExampleSeed] = useState<string | undefined>(undefined);

  const [leftRailWidth, setLeftRailWidth] = useState(() => loadRailWidth('left'));
  const [rightRailWidth, setRightRailWidth] = useState(() => loadRailWidth('right'));

  // Reset per-studio UI state on studio switch (session/document reset live in
  // useWriterLabSession).
  useEffect(() => {
    setSwatchSelections({});
    setActiveKind(null);
    setStorytellerConceptId(null);
    setActiveSection(null);
  }, [studioId]);

  // Storyteller: picking an outline section opens it for drafting — switch to
  // Generate and seed the prompt with the section synopsis (keyed so re-picking
  // the same section re-seeds).
  const handleSelectSection = useCallback((section: StoryOutlineSection) => {
    setActiveSection(section);
    setActiveKind('Generate');
    setPromptSeedKey(k => k + 1);
  }, []);

  // Blank-doc example chip → switch to Generate and seed its prompt (reuses
  // the panel's promptSeed channel; keyed so re-clicking re-seeds).
  const handleExamplePrompt = useCallback((text: string) => {
    setExampleSeed(text);
    setActiveKind('Generate');
    setPromptSeedKey(k => k + 1);
  }, []);

  // Default the active operation from the studio's featured list.
  const defaultKind = useMemo(() => (studio ? pickDefaultTextOperation(studio) : null), [studio]);
  useEffect(() => {
    if (defaultKind && !activeKind) setActiveKind(defaultKind);
  }, [defaultKind, activeKind]);

  // D-7: consume a pending Image→Caption handoff. Only the Influencer studio
  // wires the captured image into Generate as a reference.
  const pendingCaption = useCaptionHandoffStore(s => s.pending);
  useEffect(() => {
    if (studioId !== 'influencer') return;
    if (!pendingCaption) return;
    const consumed = useCaptionHandoffStore.getState().consumeHandoff();
    if (!consumed) return;
    setReferenceImageId(consumed.assetId);
    setActiveKind('Generate');
  }, [pendingCaption, studioId]);

  const studioParams = useMemo(
    () => (studio ? studioParamsFromSelections(studio.parameterPalettes, swatchSelections) : undefined),
    [studio, swatchSelections],
  );
  const swatchPrompt = useMemo(
    () => (studio ? describeSwatches(studio.parameterPalettes, swatchSelections) : ''),
    [studio, swatchSelections],
  );
  const studioRequest = useMemo(() => {
    const swatchMeta = studio
      ? buildStudioMetadata(studio.metadataSchema, studio.parameterPalettes, swatchSelections)
      : undefined;

    // Storyteller: inject the active concept + outline-section context into
    // the studio_storyteller metadata block so each chapter draft asset
    // traces back to the concept and section it was drafted from. Without
    // this the slice gate (#09) still passes — the section→draft link via
    // sections.currentDraftId works regardless — but the back-link
    // (draft.metadata.studio_storyteller.{conceptId, sectionId}) was empty,
    // breaking the scenarios canary's A4 + A6 round-trip claims.
    if (isStoryteller && studio?.metadataSchema?.metadataKey && storytellerConceptId) {
      const key = studio.metadataSchema.metadataKey;
      const swatchBlock = (swatchMeta?.[key] as Record<string, unknown> | undefined) ?? {};
      const merged: Record<string, unknown> = {
        ...swatchBlock,
        conceptId: storytellerConceptId,
        ...(activeSection?.id ? { sectionId: activeSection.id } : {}),
      };
      return {
        studioId: studio.id,
        metadata: { ...(swatchMeta ?? {}), [key]: merged },
      };
    }

    return swatchMeta ? { studioId: studio?.id, metadata: swatchMeta } : {};
  }, [studio, swatchSelections, isStoryteller, storytellerConceptId, activeSection]);

  const handleResult = useCallback(
    (result: TextOperationResult) => {
      captureDocument(result.documentId ?? result.draftId ?? null);
      // Refresh the outline so the drafted section's status / currentDraftId update.
      if (isStoryteller && storytellerConceptId) {
        qc.invalidateQueries({ queryKey: queryKeys.storytellerConcept(storytellerConceptId) });
      }
    },
    [captureDocument, isStoryteller, storytellerConceptId, qc],
  );

  if (isLoading) {
    return <LabLoadingSkeleton />;
  }

  if (error || !studio) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {error instanceof ApiResponseError
            ? `${error.code}: ${error.message}`
            : error instanceof Error
              ? error.message
              : `Studio "${studioId}" not found.`}
        </Alert>
      </Box>
    );
  }

  // Shared content nodes — same components for both shells, different
  // composition. Spike 1 polish (per `17-mobile-shell-plan-2026-05-26.md` §3):
  // the mobile shell slots these into bottom-tab views; desktop arranges
  // them as fixed left/right rails.
  const swatchesContent = (
    <>
      {isStoryteller && (
        <StorytellerRail
          studioId={studioId}
          activeConceptId={storytellerConceptId}
          onActiveConceptChange={setStorytellerConceptId}
          activeSectionId={activeSection?.id}
          onSelectSection={handleSelectSection}
        />
      )}
      <SwatchPanel
        palettes={studio.parameterPalettes}
        selections={swatchSelections}
        onSelectionsChange={setSwatchSelections}
      />
    </>
  );

  // Blank-doc starter chips — shown before any draft exists (Storyteller drives
  // drafting from its outline rail instead, so it opts out).
  const showExampleChips = !isStoryteller && !documentId;
  const canvasContent = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <CanvasToolbar
        documentTitle={isStoryteller && activeSection ? `${studio.name}-${activeSection.title}` : studio.name}
      />
      {showExampleChips && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Start from an example
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75, rowGap: 0.75 }}>
            {writerExamplePromptsForStudio(studioId).map(prompt => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                variant="outlined"
                clickable
                onClick={() => handleExamplePrompt(prompt)}
                sx={{
                  maxWidth: 320,
                  // A MUI Chip is a fixed-height pill (24px at size="small").
                  // Letting the label wrap without releasing that height makes
                  // multi-line prompts spill straight through the outline —
                  // visible on Blogger, whose examples are the longest. Grow
                  // with the content instead, matching the Image Lab chips.
                  height: 'auto',
                  minHeight: 28,
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    display: 'block',
                    py: 0.5,
                    lineHeight: 1.3,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, p: 1 }}>
        <LexicalDocCanvas />
      </Box>
    </Box>
  );

  // Gap 4 — featured-agent cold-start UX. Seed the agent's Instruction
  // textarea with the active section's synopsis (Storyteller) so that
  // e.g. `chapter-structure-agent` or `story-concept-agent` is one click
  // from useful output. The other studios fall through to the studio
  // name as a generic hint.
  const defaultAgentInstruction =
    isStoryteller && activeSection?.synopsis
      ? activeSection.synopsis
      : `Help me with my ${studio.name} draft.`;

  const operationsContent = (
    <>
      <SelectionChips
        palettes={studio.parameterPalettes}
        selections={swatchSelections}
        onSelectionsChange={setSwatchSelections}
      />
      {activeKind && (
        <Box sx={{ p: 1 }}>
          <StudioRequestContext.Provider value={studioRequest}>
            <TextOperationPanel
              kind={activeKind}
              ensureSession={ensureSession}
              isCreatingSession={isCreatingSession}
              documentId={documentId}
              swatchPrompt={swatchPrompt}
              studioParams={studioParams}
              referenceImageId={referenceImageId}
              sectionId={isStoryteller ? activeSection?.id : undefined}
              promptSeed={isStoryteller ? activeSection?.synopsis : exampleSeed}
              promptSeedKey={promptSeedKey}
              onResult={handleResult}
            />
          </StudioRequestContext.Provider>
        </Box>
      )}
      {activeKind && (
        <Box sx={{ mt: 'auto', borderTop: theme => `1px solid ${theme.palette.divider}` }}>
          <TextOperationDock studio={studio} activeKind={activeKind} onSelect={setActiveKind} />
        </Box>
      )}
    </>
  );

  const agentBar = (
    <WriterLabAgentBar
      sessionId={sessionId}
      labId={studio.labId}
      labName={studio.labId === 'writer-lab' ? "Writer's Lab" : studio.labId}
      studioId={studioId}
      studioName={studio.name}
      activeOperationKind={activeKind ?? undefined}
      defaultModel={studio.defaultLlmModel}
    />
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Mobile shell hides the desktop TopBar (it owns its own AppBar). Keep
          the error alert above the LexicalComposer in both branches so a
          session-start failure surfaces uniformly. */}
      {!isMobileShell && <TopBar labId={studio.labId} studioId={studioId} />}
      {sessionError && (
        <Alert severity="error" sx={{ m: 1 }}>
          {`Couldn't start a session: ${sessionError.message}`}
        </Alert>
      )}

      <LexicalComposer key={studioId} initialConfig={buildInitialConfig()}>
        {isMobileShell ? (
          // Mobile shell: per-tab slot bodies. Each tab fills the viewport;
          // the AgentBar persists as a slim strip above the bottom tabs.
          // The LexicalComposer stays outside MobileShell so the editor
          // context is shared between canvasSlot and actionSlot (the op
          // panels dispatch AI_INSERT into the editor).
          <MobileShell
            title={`${studio.labId === 'writer-lab' ? "Writer's Lab" : studio.labId} · ${studio.name}`}
            canvasSlot={canvasContent}
            swatchesSlot={
              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {swatchesContent}
              </Box>
            }
            actionSlot={operationsContent}
            agentBarSlot={agentBar}
          />
        ) : (
          <>
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              {/* Left rail — studio swatches. */}
              <Box
                sx={{
                  width: leftRailWidth,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: theme => `1px solid ${theme.palette.divider}`,
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {swatchesContent}
              </Box>

              <RailSplitter
                side="left"
                width={leftRailWidth}
                onChange={setLeftRailWidth}
                onCommit={w => saveRailWidth('left', w)}
              />

              {/* Center — the Lexical document canvas (with export toolbar above it). */}
              {canvasContent}

              <RailSplitter
                side="right"
                width={rightRailWidth}
                onChange={setRightRailWidth}
                onCommit={w => saveRailWidth('right', w)}
              />

              {/* Right rail — Operations + Agents tabs. */}
              <ActionPanel
                studio={studio}
                width={rightRailWidth}
                defaultAgentInstruction={defaultAgentInstruction}
                operationsContent={operationsContent}
              />
            </Box>

            {agentBar}
          </>
        )}
      </LexicalComposer>
    </Box>
  );
};

/**
 * AgentBar shim that reads the live Lexical editor text on each chat send,
 * so the chat can answer "critique the blog above" / "tighten what I just
 * wrote" without the user re-pasting. Lives inside `<LexicalComposer>` so
 * `useLexicalComposerContext` resolves; the parent component (which owns
 * the composer mount) can't read the editor directly.
 *
 * Image Lab uses the bare AgentBar — its canvas isn't text.
 */
type WriterLabAgentBarProps = React.ComponentProps<typeof AgentBar>;

const WriterLabAgentBar: React.FC<WriterLabAgentBarProps> = props => {
  const [editor] = useLexicalComposerContext();
  const getCurrentDocument = useCallback((): string | undefined => {
    let text = '';
    editor.getEditorState().read(() => {
      text = $getRoot().getTextContent();
    });
    return text.trim() ? text : undefined;
  }, [editor]);
  return <AgentBar {...props} getCurrentDocument={getCurrentDocument} />;
};
